import { useState } from "react";
import { faHome, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "../Link";

// // // //

interface Props {
    pages: Array<{ name: string; href?: string; current: boolean }>;
}

export function Breadcrumbs(props: Props) {
    const { pages } = props;
    const [isOpen, setIsOpen] = useState(false);

    if (pages.length === 0) {
        return null;
    }

    const lastPage = pages[pages.length - 1];
    const otherPages = pages.slice(0, -1).reverse();

    return (
        <nav
            className="flex border-b bg-sidebar sticky top-nav-height z-240 select-none"
            aria-label="Breadcrumb"
        >
            {isOpen && (
                <div className="md:hidden absolute top-full mt-0 w-full bg-sidebar border-y shadow-lg z-50">
                    <ul className="py-1">
                        {otherPages.map((page) => (
                            <li key={page.name}>
                                {page.href && (
                                    <Link
                                        href={page.href}
                                        analyticsLabel={`Breadcrumb - ${page.name}`}
                                        className="block px-4 py-2 text-sm text-primary hover:bg-slate-50"
                                    >
                                        {page.name}
                                    </Link>
                                )}
                            </li>
                        ))}
                        {/* Home item in dropdown */}
                        <li>
                            <Link
                                href="/"
                                analyticsLabel="Breadcrumb - Home"
                                className="flex items-center px-4 py-2 text-sm text-primary hover:bg-slate-50"
                            >
                                <FontAwesomeIcon
                                    icon={faHome}
                                    className="h-4 w-4 mr-2"
                                    aria-hidden="true"
                                />
                                Home
                            </Link>
                        </li>
                    </ul>
                </div>
            )}

            <ol
                role="list"
                className="flex w-full max-w-(--breakpoint-xl) md:space-x-4 px-4 sm:px-6 lg:px-8"
            >
                {/* Home button - visible only on desktop */}
                <li className="hidden md:flex">
                    <div className="flex items-center">
                        <Link
                            analyticsLabel="Breadcrumbs - Home"
                            href="/"
                            className="text-primary hover:text-blue-700"
                        >
                            <FontAwesomeIcon
                                icon={faHome}
                                className="h-5 w-5 shrink-0"
                                aria-hidden="true"
                            />
                            <span className="sr-only">Home</span>
                        </Link>
                    </div>
                </li>

                {/* Mobile dropdown */}
                <li className="md:hidden relative flex items-center">
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="p-1 rounded-sm hover:bg-slate-100 transition-colors duration-200 flex items-center"
                        aria-label="Show full breadcrumb"
                    >
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className="h-4 w-4 text-slate-500"
                        />
                    </button>
                </li>

                {/* Desktop breadcrumbs */}
                {otherPages.length > 0 && (
                    <li className="hidden md:flex items-center space-x-4">
                        {otherPages.reverse().map((page) => (
                            <div key={page.name} className="flex items-center">
                                <svg
                                    className="h-full w-6 shrink-0 text-border"
                                    viewBox="0 0 24 44"
                                    preserveAspectRatio="none"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
                                </svg>
                                {page.href && (
                                    <Link
                                        href={page.href}
                                        analyticsLabel={`Breadcrumb - ${page.name}`}
                                        className="ml-4 text-sm text-primary hover:text-blue-700"
                                    >
                                        {page.name}
                                    </Link>
                                )}
                                {!page.href && (
                                    <span className="ml-4 text-sm text-muted-foreground">
                                        {page.name}
                                    </span>
                                )}
                            </div>
                        ))}
                    </li>
                )}

                {/* Last item (always visible) */}
                <li className="flex items-center">
                    <svg
                        className="h-full w-6 shrink-0 text-border"
                        viewBox="0 0 24 44"
                        preserveAspectRatio="none"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
                    </svg>
                    <span
                        className="ml-4 text-sm text-muted-foreground truncate max-w-[200px] lg:max-w-none"
                        aria-current={lastPage.current ? "page" : undefined}
                    >
                        {lastPage.name}
                    </span>
                </li>
            </ol>
        </nav>
    );
}
